/* SpaceArmory PDP v2: review link, manual measurement calculator, size-range check.
   Purchase state lives in SpaceArmoryPurchaseController (global.js); this file only drives v2 extras. */
(() => {
  if (window.SpaceArmoryPdpV2) return;
  const reducedMotion = () => window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  // Text in the shopper's language: snippets/sa-i18n-js.liquid sets window.SA_I18N on French, Spanish and German pages
  // only. English pages have no dictionary, so every string falls back to its English text unchanged.
  const calcText = (key, fallback, vars = null) => {
    let text = window.SA_I18N?.[key] ?? fallback;
    if (vars) for (const [name, value] of Object.entries(vars)) text = text.replaceAll(`{${name}}`, value);
    return text;
  };
  // French, Spanish and German pages write decimals with a comma (13,5); English pages keep the point.
  const calcDecimal = value => (window.SA_I18N ? String(value).replace(/(\d)\.(\d)/g, '$1,$2') : String(value));

  // Judge.me review widget anchor.
  document.addEventListener('click', event => {
    const link = event.target.closest('[data-sa-v2-reviews-link]');
    if (!link) return;
    const widget = document.getElementById('judgeme_product_reviews') || document.querySelector('.jdgm-review-widget, .jdgm-widget.jdgm-review-widget');
    if (!widget) return;
    event.preventDefault();
    widget.scrollIntoView({behavior: reducedMotion() ? 'auto' : 'smooth', block: 'start'});
  });

  // The visitor's sizing unit, decided once in Liquid (snippets/sa-size-unit.liquid: inches on the United States
  // country, cm elsewhere) and passed on face-fit-scanner[data-size-unit]. Without it (a page rendered before that
  // attribute existed) the ranges stay in cm, as that page printed them.
  function sizeUnit(element) {
    const root = element?.closest?.('product-info')?.querySelector('face-fit-scanner[data-size-unit]') || document.querySelector('face-fit-scanner[data-size-unit]');
    return root?.dataset.sizeUnit === 'in' ? 'in' : 'cm';
  }
  // A size range as the tiles print it: "14–15 cm", or the same edges in inches to one decimal, "5.5–5.9 in" (the
  // chart's inch row: 12 / 13 / 14 / 15 / 16 / 17 cm read 4.7 / 5.1 / 5.5 / 5.9 / 6.3 / 6.7 in).
  function rangeText(range, unit) {
    return unit === 'in' ? `${(range.min / 2.54).toFixed(1)}–${(range.max / 2.54).toFixed(1)} in` : `${range.min}–${range.max} cm`;
  }

  // Size ranges come from the canonical rules; correct the server-rendered labels if they drift.
  function ranges() {
    const rules = window.SpaceArmoryFitRules;
    if (!rules) return null;
    const found = {};
    for (let tenth = 100; tenth <= 200; tenth++) {
      const cm = tenth / 10;
      const size = rules.sizeFor(cm);
      if (!size) continue;
      found[size] ??= {min: cm, max: cm};
      found[size].max = cm;
    }
    Object.values(found).forEach(range => { range.max = Math.ceil(range.max); });
    return found;
  }
  function verifyRanges(scope = document) {
    const found = ranges();
    if (!found) return;
    scope.querySelectorAll('[data-sa-v2-range]').forEach(label => {
      const key = {SMALL: 'S', MEDIUM: 'M', LARGE: 'L', 'X-LARGE': 'XL', 'EXTRA LARGE': 'XL', '2XL': 'XXL', 'XX-LARGE': 'XXL'}[label.dataset.saV2Range] || label.dataset.saV2Range;
      const range = found[key];
      if (!range) return;
      const text = rangeText(range, sizeUnit(label));
      if (label.textContent !== text) label.textContent = text;
    });
  }

  // Manual guide: type the measured length, get the size, apply it through the purchase controller. The guide opens on
  // the visitor's unit (manual-size-guide.liquid); the result speaks the unit of the tab the width was typed in.
  function unitOf(guide) {
    return guide.querySelector('[data-unit][aria-selected="true"]')?.dataset.unit === 'cm' ? 'cm' : 'in';
  }
  // What the shopper typed, read as a whole: {value, shown, unit} or null. A decimal ("5.5", "5,5", "5."), a fraction
  // ("5 1/2", "5-1/2", "5½", "1/2"; US tape measures and the guide's ruler read inches in eighths), optionally followed by
  // a unit ("in", "inches", '"', "cm"). shown is the number the way the shopper wrote it, for the result line and the
  // size card: "5.5" stays "5.5", "5.49" stays "5.49", "5-1/2" reads "5 1/2", "5½" stays "5½" (a leading "+" or zeros
  // go). Anything else is not read at all, so a size never comes from part of the input (parseFloat read "5 5/8" as 5).
  const glyphs = {'½': 1 / 2, '⅓': 1 / 3, '⅔': 2 / 3, '¼': 1 / 4, '¾': 3 / 4, '⅕': 1 / 5, '⅖': 2 / 5, '⅗': 3 / 5, '⅘': 4 / 5, '⅙': 1 / 6, '⅚': 5 / 6, '⅛': 1 / 8, '⅜': 3 / 8, '⅝': 5 / 8, '⅞': 7 / 8};
  function readWidth(text) {
    let rest = String(text).replace(/\u2044/g, '/').replace(/\s+/g, ' ').trim();
    let unit = null;
    const suffix = rest.match(/\s*(inches|inch|in\.?|"|''|\u2033|\u201d|centimeters?|centimetres?|cm)$/i);
    if (suffix) { unit = /^c/i.test(suffix[1]) ? 'cm' : 'in'; rest = rest.slice(0, suffix.index); }
    rest = rest.replace(/^\+/, '');
    const whole = digits => String(Number(digits));
    let match = rest.match(/^(\d+(?:[.,]\d*)?|[.,]\d+)$/);
    if (match) {
      const number = match[1].replace(',', '.');
      return {value: Number(number), shown: number.replace(/\.$/, '').replace(/^\./, '0.').replace(/^0+(?=\d)/, ''), unit};
    }
    match = rest.match(/^(?:(\d+)(?: ?- ?| ))?(\d+) ?\/ ?(\d+)$/);
    if (match) {
      const [, base, top, bottom] = match;
      // "5 9/8" or "5 1/0" is not a measurement a ruler gives.
      if (!Number(bottom) || (base && Number(top) >= Number(bottom))) return null;
      return {value: (base ? Number(base) : 0) + Number(top) / Number(bottom), shown: `${base ? `${whole(base)} ` : ''}${whole(top)}/${whole(bottom)}`, unit};
    }
    match = rest.match(new RegExp(`^(?:(\\d+) ?-? ?)?([${Object.keys(glyphs).join('')}])$`));
    if (match) return {value: (match[1] ? Number(match[1]) : 0) + glyphs[match[2]], shown: `${match[1] ? whole(match[1]) : ''}${match[2]}`, unit};
    return null;
  }
  // Inches are sized on the chart's inch row, from the number as typed: S 4.7–5.1, M 5.1–5.5, L 5.5–5.9, XL 5.9–6.3,
  // XXL 6.3–6.7, the upper size on a boundary (owner, 10 Oct 2026), so the size always sits in the row of the inches
  // the result shows. Through cm, 5.89 in (14.96 cm, printed 15.0 cm) would read XL next to "5.89 in", although the
  // chart puts 5.89 in L. For every inch value the chart prints (one decimal) both give the same size.
  function inchSize(inches) {
    if (!Number.isFinite(inches) || inches < 4.7 || inches > 6.7) return null;
    return inches < 5.1 ? 'S' : inches < 5.5 ? 'M' : inches < 5.9 ? 'L' : inches < 6.3 ? 'XL' : 'XXL';
  }
  // The size of a width in a unit: inches on the inch row, cm from the cm as it prints (face-fit-rules.js: 12.95 cm
  // prints 13.0 cm and is M). The two charts never overlap (4.7–6.7 in, 12–17 cm).
  const sizeIn = (unit, value) => unit === 'in' ? inchSize(value) : window.SpaceArmoryFitRules?.sizeFor(value) || null;
  // A width typed in a unit that belongs to the other tab: with that unit after it, or a bare number that fits only the
  // other tab's chart (a US shopper reading the ruler's cm scale types "14" on INCHES; 14 in is no face).
  const otherTabWidth = (typed, unit, other) => !!typed && (typed.unit === other || (!typed.unit && !sizeIn(unit, typed.value) && !!sizeIn(other, typed.value)));
  // What the shopper typed and on which tab, per guide: {text, unit, converted}. converted is the same width as the other
  // tab shows it after a switch (switchUnit).
  const typedIn = new WeakMap();
  function updateCalc(guide) {
    const calc = guide.querySelector('[data-sa-v2-calc]');
    if (!calc) return;
    const input = calc.querySelector('[data-sa-v2-calc-input]');
    const result = calc.querySelector('[data-sa-v2-calc-result]');
    const use = calc.querySelector('[data-sa-v2-calc-use]');
    const offer = calc.querySelector('[data-sa-v2-calc-switch]');
    const tab = unitOf(guide);
    // The width converted from the tab it was typed on: the size, the cm and the messages still come from what the
    // shopper typed, so switching tabs never changes the size (5.89 in is L on the inch row; its 15.0 cm reads XL).
    const own = typedIn.get(guide);
    const converted = !!own && own.unit !== tab && own.converted === input.value;
    const unit = converted ? own.unit : tab;
    const other = unit === 'in' ? 'cm' : 'in';
    calc.querySelector('[data-sa-v2-calc-unit]').textContent = tab;
    input.placeholder = calcText('calc.example', 'e.g. {value}', {value: calcDecimal(tab === 'cm' ? '13.5' : '5.3')});
    const typed = readWidth(converted ? own.text : input.value);
    const value = typed ? typed.value : NaN;
    // A width in the other unit: the answer is that tab, never "choose XXL" or "choose S" for an L face. A number typed
    // with this tab's unit ("14 in") is read as written.
    const switchTo = !converted && otherTabWidth(typed, unit, other) ? other : '';
    const size = typed && !switchTo ? sizeIn(unit, value) : null;
    // The cm handed to the size card: as typed, or for inches converted and kept inside the printed chart edges (4.7 in
    // is 11.94 cm and 6.7 in 17.02 cm; the chart prints them as the S and XXL edges, 12 and 17 cm).
    const cm = size ? (unit === 'in' ? Math.min(17, Math.max(12, value * 2.54)) : value) : NaN;
    // In the unit it was typed in: inches as typed ("5.5 in", "5 5/8 in"), cm as the size rules print them ("14.0 cm"
    // for 13.95). The size card shows this text (useManual in global.js), also after a tab switch; the result line shows
    // the tab's unit, the converted width after a switch.
    const typedShown = size ? (unit === 'in' ? typed.shown : window.SpaceArmoryFitRules.display(cm).cm) : '';
    const shown = size && converted ? input.value : typedShown;
    guide.querySelectorAll('.manual-size-guide__table tbody tr').forEach(row => {
      row.classList.toggle('sa-v2-calc-hit', !!size && row.querySelector('th')?.textContent.trim() === size);
    });
    calc.dataset.size = size || '';
    calc.dataset.cm = size ? String(cm) : '';
    // (Not data-unit: that marks the unit tabs.)
    calc.dataset.shownUnit = size ? unit : '';
    calc.dataset.shownValue = typedShown;
    calc.dataset.switchTo = switchTo;
    use.hidden = !size;
    if (offer) {
      offer.hidden = !switchTo;
      offer.textContent = switchTo ? (switchTo === 'cm' ? calcText('calc.switch_cm', 'Switch to CM') : calcText('calc.switch_in', 'Switch to Inches')) : '';
    }
    if (!input.value.trim()) { result.textContent = ''; return; }
    if (size) {
      result.innerHTML = '';
      const strong = document.createElement('strong');
      strong.textContent = size;
      result.append(calcText('calc.your_size', 'Your Size: '), strong, ` (${calcDecimal(shown)} ${tab})`);
      use.textContent = calcText('calc.use_size', 'Use Size {size}', {size});
    } else if (switchTo) {
      result.textContent = switchTo === 'cm' ? calcText('calc.looks_cm', 'That looks like centimeters, not inches.') : calcText('calc.looks_in', 'That looks like inches, not centimeters.');
    } else if (typed) {
      const under = unit === 'in' ? value < 4.7 : value < 12;
      result.textContent = under ? calcText('calc.under', 'Under {limit}. Please measure again, or choose S.', {limit: calcDecimal(tab === 'in' ? '4.7 in' : '12 cm')}) : calcText('calc.over', 'Over {limit}. Please measure again, or choose XXL.', {limit: calcDecimal(tab === 'in' ? '6.7 in' : '17 cm')});
    } else {
      result.textContent = calcText('calc.enter_number', 'Enter a number, e.g. {value}', {value: calcDecimal(tab === 'cm' ? '13.5' : '5.3')});
    }
  }
  // INCHES/CM (owner, 11 Oct 2026): a switch converts the width shown, 5.5 in reads 14.0 cm and 14.4 cm reads 5.67 in
  // (cm and inches as every page prints them, face-fit-rules.js display), and switching back shows exactly what was
  // typed ("5 1/2" stays "5 1/2"). A width that belongs to the new tab ("14" typed on INCHES) is read there as typed,
  // as "Switch to CM" does. Without the size rules the number is read in the new tab as before.
  function switchUnit(guide) {
    const input = guide.querySelector('[data-sa-v2-calc-input]');
    const own = typedIn.get(guide);
    const tab = unitOf(guide);
    const rules = window.SpaceArmoryFitRules;
    if (input && own && own.text.trim()) {
      const typed = readWidth(own.text);
      if (own.unit !== tab && typed && rules && !otherTabWidth(typed, own.unit, tab)) {
        const cm = own.unit === 'in' ? typed.value * 2.54 : typed.value;
        // Inches the chart sizes are kept inside its printed edges, as the size card's cm is (4.7 in prints 12.0 cm).
        own.converted = own.unit === 'in' ? rules.display(sizeIn('in', typed.value) ? Math.min(17, Math.max(12, cm)) : cm).cm : rules.display(cm).inches;
        input.value = own.converted;
      } else {
        if (own.unit !== tab) Object.assign(own, {unit: tab, converted: undefined});
        if (input.value !== own.text) input.value = own.text;
      }
    }
    updateCalc(guide);
  }
  // Measure Again on the size card (owner, 11 Oct 2026): the guide opens clean the next time, with no width, result,
  // Use Size or marked chart row. The unit tab stays as the shopper left it.
  function clearCalc(guide) {
    const input = guide.querySelector('[data-sa-v2-calc-input]');
    if (!input) return;
    typedIn.delete(guide);
    input.value = '';
    updateCalc(guide);
  }
  document.addEventListener('input', event => {
    const guide = event.target.closest('[data-sa-v2-calc-input]') && event.target.closest('manual-size-guide');
    if (!guide) return;
    typedIn.set(guide, {text: event.target.value, unit: unitOf(guide)});
    updateCalc(guide);
  });
  // Enter, or the phone keyboard's Go / Done key, in the measurement field does what Use Size does while a size is
  // shown (owner, 11 Oct 2026); otherwise it does nothing.
  document.addEventListener('keydown', event => {
    if (event.key !== 'Enter' || event.isComposing) return;
    const guide = event.target.closest?.('[data-sa-v2-calc-input]') && event.target.closest('manual-size-guide');
    const use = guide?.querySelector('[data-sa-v2-calc-use]');
    if (!use || use.hidden || !guide.querySelector('[data-sa-v2-calc]')?.dataset.size) return;
    event.preventDefault();
    use.click();
  });
  // The unit tabs also switch with the arrow keys, Home and End (manual-size-guide.liquid); the calculator follows.
  document.addEventListener('keydown', event => {
    const guide = event.target.closest?.('[data-unit]') && event.target.closest('manual-size-guide');
    if (guide && guide.querySelector('[data-sa-v2-calc]') && ['ArrowLeft', 'ArrowRight', 'Home', 'End'].includes(event.key)) requestAnimationFrame(() => switchUnit(guide));
  });
  document.addEventListener('click', event => {
    if (event.target.closest('[data-purchase-again]')) {
      const guide = event.target.closest('product-info')?.querySelector('manual-size-guide');
      if (guide) clearCalc(guide);
      return;
    }
    const guide = event.target.closest('manual-size-guide');
    if (!guide || !guide.querySelector('[data-sa-v2-calc]')) return;
    if (event.target.closest('[data-unit]')) { requestAnimationFrame(() => switchUnit(guide)); return; }
    const calc = guide.querySelector('[data-sa-v2-calc]');
    // "Switch to CM" / "Switch to Inches": the same as tapping that tab (manual-size-guide.liquid switches the chart),
    // then the same number is read in that unit and the shopper lands on Use Size.
    if (event.target.closest('[data-sa-v2-calc-switch]')) {
      guide.querySelector(`[data-unit="${calc.dataset.switchTo}"]`)?.click();
      updateCalc(guide);
      const next = calc.querySelector('[data-sa-v2-calc-use]:not([hidden])') || calc.querySelector('[data-sa-v2-calc-input]');
      next?.focus({preventScroll: true});
      return;
    }
    if (!event.target.closest('[data-sa-v2-calc-use]')) return;
    const size = calc.dataset.size;
    const purchase = guide.closest('product-info')?.faceFitPurchase;
    if (!size || !purchase) return;
    const result = guide.querySelector('[data-sa-v2-calc-result]');
    try {
      purchase.applySize(size);
      // The size card shows this hand measurement on this page only, in the unit it was typed in; it never follows to
      // other masks.
      const cm = Number(calc.dataset.cm);
      if (calc.dataset.cm && Number.isFinite(cm)) purchase.useManual?.(cm, {unit: calc.dataset.shownUnit, value: calc.dataset.shownValue, size});
      guide.close?.();
    } catch (error) {
      result.textContent = error.message === 'variant_unavailable' ? calcText('calc.size_sold_out', 'Size {size} is sold out.', {size}) : calcText('calc.size_unavailable', "Size {size} isn't available for this mask.", {size});
    }
  });

  window.SpaceArmoryPdpV2 = {verifyRanges};
  const init = () => verifyRanges();
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', init); else init();
  // Variant changes re-render the picker from the server; re-check after each swap.
  document.addEventListener('change', event => {
    if (event.target.closest('variant-selects')) setTimeout(() => verifyRanges(), 400);
  });
})();
